import { useMutationObserver } from "@vueuse/core";
import { computed, watch } from "vue";

import { extendUrlParams, toAbsoluteUrl, toOriginPath } from "@solvari/utils";

import type { ClientFeature } from "@/client/features.ts";

import { whenAbTestingLoaded } from "@/client/loaded.ts";
import { getAbTestFeature } from "@/client/reader.ts";

function linkMapFeature({
  applyParams = extendUrlParams,
  root = document,
}: {
  applyParams?: typeof extendUrlParams;
  root?: Document | HTMLElement;
} = {}): ClientFeature {
  return async (_instance, { domReady }) => {
    await whenAbTestingLoaded();
    // Vue hydration resets anchors rewritten before it — the observer catches later inserts.
    await domReady;

    const linkMap = getAbTestFeature<Record<string, string>>("link-map");

    const targetByOriginPath = computed(
      () =>
        new Map(
          Object.entries(linkMap.value ?? {}).flatMap(([source, target]) => {
            const originPath = toOriginPath(source, window.location.origin);
            return originPath ? [[originPath, target] as const] : [];
          }),
        ),
    );

    // Rewrite once per anchor: a cyclic or chained map would otherwise re-trigger the observer forever
    const rewritten = new WeakSet<HTMLAnchorElement>();

    function apply() {
      const byPath = targetByOriginPath.value;
      if (byPath.size === 0) {
        return;
      }

      root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((anchor) => {
        const href = anchor.getAttribute("href");
        if (!href || href.startsWith("#") || rewritten.has(anchor)) {
          return;
        }

        const originPath = toOriginPath(anchor.href);
        const target = originPath ? byPath.get(originPath) : undefined;
        if (target) {
          const next = applyParams(
            toAbsoluteUrl(target, new URL(anchor.href).origin),
          );
          if (anchor.href !== next) {
            rewritten.add(anchor);
            anchor.href = next;
          }
        }
      });
    }

    const target = root instanceof Document ? root.body : root;
    useMutationObserver(target, apply, {
      childList: true,
      subtree: true,
      attributeFilter: ["href"],
    });

    watch(linkMap, apply, { immediate: true, deep: true });
  };
}

export { linkMapFeature };
